import React, { useState, useEffect, useCallback, useMemo, memo } from 'react'
import { Tabs, Tab, usePageWidth } from '@fs/zion-ui'
import { useParams } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import type { TempleRollupStatus } from '@fs/zion-tree-types'
import { useNonReligiousMode, usePermission } from '@fs/zion-permissions'
import { i18n } from '@fs/zion-locale'
import useFocusPerson from '../../data-service/useFocusPerson'
import OrdinanceBadge from './OrdinanceBadge'
import useActiveRole from '../../common/useActiveRole'

type Props = {
  showTemple: boolean
}

const ordinanceLabelKeyMap: Record<Exclude<TempleRollupStatus, 'CALCULATING'>, string> = {
  READY: 'tree-person.ordinance-tab.ready.aria',
  RESERVED_SHARED_READY: 'tree-person.ordinance-tab.ready.aria',
  RESERVED: 'tree-person.ordinance-tab.ready.aria',
  IN_PROGRESS: 'tree-person.ordinance-tab.ready.aria',
  NEEDS_MORE_INFORMATION: 'tree-person.ordinance-tab.not-ready.aria',
  COMPLETED: 'tree-person.ordinance-tab.completed.aria',
  NOT_AVAILABLE: 'tree-person.ordinance-tab.not-available.aria',
}

const MOBILE_VITALS_TAB = 'Mobile Vitals Tab'
const MOBILE_NON_VITALS_TAB = 'Mobile Non-Vitals Tab'
const MOBILE_MEMORIES_TAB = 'Mobile Memories Tab'
const MOBILE_ORDINANCES_TAB = 'Mobile Ordinances Tab'
function PersonPageTabs({ showTemple }: Props): JSX.Element | null {
  const [t] = useTranslation()
  const pageWidth = usePageWidth()
  const { role } = useActiveRole()
  const isFSAdmin = !!role
  const isMobile = pageWidth({ lg: false, default: true })
  const { pid, subPage } = useParams()
  const { focusPerson } = useFocusPerson()

  const [nonReligiousModeCheck, status, error] = useNonReligiousMode()
  const nonReligiousMode = status === 'loading' || error !== null || nonReligiousModeCheck

  const [isCountryEnabled, isCountryEnabledStatus] = usePermission('CanAddPersonalDataPermission', 'FamilyTree')
  const memoriesEnabled = isCountryEnabledStatus === 'loaded' && isCountryEnabled

  const tabs = useMemo(() => {
    if (!focusPerson) return []
    const isAvailable = (key): boolean => Boolean(i18n.t(key, { defaultValue: ' ', fallbackLng: 'none' }).trim())
    const ordinanceLabel =
      (focusPerson.templeRollupStatus &&
        isAvailable(ordinanceLabelKeyMap[focusPerson.templeRollupStatus]) &&
        t(ordinanceLabelKeyMap[focusPerson.templeRollupStatus])) ||
      ''
    const sourcesTitle = `${t('tree-person.sources.label')} (${focusPerson.sourceCount})`
    const memoriesTitle = `${t('tree-person.memories.label')} (${focusPerson.memoryCount})`

    const showOrdinanceBadge = !focusPerson.living || isFSAdmin

    let realTabs = isMobile
      ? [
          <Tab key="about-tab" linkName="Mobile About Tab" to={`/about/${pid}`} title={t('tree-person.about.label')} />,
          <Tab
            key="vitals-tab"
            linkName={MOBILE_VITALS_TAB}
            to={`/vitals/${pid}`}
            title={t('tree-person.vitals.label')}
          />,
          <Tab
            key="non-vitals-tab"
            linkName={MOBILE_NON_VITALS_TAB}
            to={`/non-vitals/${pid}`}
            title={t('tree-person.other.label')}
          />,
          <Tab
            key="family-tab"
            linkName="Mobile Family Tab"
            to={`/family/${pid}`}
            title={t('tree-person.family.header')}
          />,
          <Tab key="sources-tab" linkName="Mobile Sources Tab" to={`/sources/${pid}`} title={sourcesTitle} />,
          <Tab
            key="collaborate-tab"
            linkName="Mobile Collaborate Tab"
            to={`/collaborate/${pid}`}
            title={t('tree-person.collaborate-count.label', { count: focusPerson.collaborateCount })}
            data-testid="collaborate-tab"
          />,
          memoriesEnabled ? (
            <Tab key="memories-tab" linkName={MOBILE_MEMORIES_TAB} to={`/memories/${pid}`} title={memoriesTitle} />
          ) : null,
          <Tab
            data-testid="time-line-tab"
            key="timeline-tab"
            linkName="Mobile Timeline Tab"
            to={`/timeline/${focusPerson.id}`}
            title={t('tree-person.timeline.header')}
          />,
          <Tab
            key="ordinances-tab"
            linkName={MOBILE_ORDINANCES_TAB}
            to={`/ordinances/${pid}`}
            title={t('tree-person.ordinances.label')}
          />,
        ]
      : [
          <Tab
            key="about-tab"
            linkName="Desktop About Tab"
            to={`/about/${pid}`}
            title={t('tree-person.about.label')}
            data-testid="about-tab"
          />,
          <Tab
            key="details-tab"
            linkName="Desktop Details Tab"
            to={`/details/${pid}`}
            title={t('tree-person.details.label')}
            data-testid="details-tab"
          />,
          <Tab
            key="sources-tab"
            linkName="Desktop Sources Tab"
            to={`/sources/${pid}`}
            title={sourcesTitle}
            data-testid="sources-tab"
          />,
          <Tab
            key="collaborate-tab"
            linkName="Desktop Collaborate Tab"
            to={`/collaborate/${pid}`}
            title={t('tree-person.collaborate-count.label', { count: focusPerson.collaborateCount })}
            data-testid="collaborate-tab"
          />,
          memoriesEnabled ? (
            <Tab
              key="memories-tab"
              linkName="Desktop Memories Tab"
              to={`/memories/${pid}`}
              title={memoriesTitle}
              data-testid="memories-tab"
            />
          ) : null,
          <Tab
            data-testid="time-line-tab"
            key="timeline-tab"
            linkName="Desktop Timeline Tab"
            to={`/timeline/${focusPerson.id}`}
            title={t('tree-person.timeline.header')}
          />,
          <Tab
            aria-label={showOrdinanceBadge ? ordinanceLabel : undefined}
            key="ordinances-tab"
            linkName="Desktop Ordinances Tab"
            to={`/ordinances/${pid}`}
            // @ts-expect-error -- We purposefully hacked in the OrdinanceBadge here.
            title={
              <span>
                {showOrdinanceBadge && (
                  <OrdinanceBadge status={focusPerson.templeRollupStatus} label={ordinanceLabel} />
                )}
                {t('tree-person.ordinances.label')}
              </span>
            }
            data-testid="ordinances-tab"
          />,
        ]

    // dont show About, Sources, collaborate and timeline tabs when deleted
    if (focusPerson.deleted) realTabs = [...realTabs.slice(1, 2), realTabs[4], realTabs[6]]

    if (focusPerson.deleted && isMobile)
      realTabs = [
        <Tab
          key="vitals-tab"
          linkName={MOBILE_VITALS_TAB}
          to={`/vitals/${pid}`}
          title={t('tree-person.vitals.label')}
        />,
        <Tab
          key="non-vitals-tab"
          linkName={MOBILE_NON_VITALS_TAB}
          to={`/non-vitals/${pid}`}
          title={t('tree-person.other.label')}
        />,
        <Tab key="memories-tab" linkName={MOBILE_MEMORIES_TAB} to={`/memories/${pid}`} title={memoriesTitle} />,
        <Tab
          key="ordinances-tab"
          linkName={MOBILE_ORDINANCES_TAB}
          to={`/ordinances/${pid}`}
          title={t('tree-person.ordinances.label')}
        />,
      ]

    // don't show ordinances tab
    if (focusPerson.deleted || !showTemple || nonReligiousMode || focusPerson.researcherTree) realTabs.pop()

    // Because we are using memoriesEnabled and can add null in the array we need to filter it out for the index selection to work
    return realTabs.filter(Boolean)
  }, [focusPerson, isFSAdmin, isMobile, memoriesEnabled, nonReligiousMode, pid, showTemple, t])

  const getSelectedTabIndex = useCallback(
    (): number => tabs.findIndex((tab) => (tab?.key as string)?.includes(subPage)),
    [subPage, tabs]
  )

  // This scrolls to the top of the page each time the tab changes
  const handleTabChange = (): void => {
    document.querySelector('header')?.scrollIntoView({ behavior: 'smooth' })
  }

  const [selected, setSelected] = useState(getSelectedTabIndex())

  useEffect(() => {
    setSelected(getSelectedTabIndex())
  }, [subPage, getSelectedTabIndex])

  if (!focusPerson) return null

  return (
    <Tabs centered index={selected} data-testid="person-page-tabs" onChange={handleTabChange}>
      {tabs}
    </Tabs>
  )
}

export default memo(PersonPageTabs)
